import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth, formatApiError } from "@/lib/auth";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const ok = searchParams.get("ok");
    const err = searchParams.get("error");

    if (err) {
      const msg = err === "google_cancelled" ? "Sign-in cancelled." : "Google sign-in failed. Please try again.";
      setError(msg);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
      return;
    }

    if (!ok) {
      navigate("/login", { replace: true });
      return;
    }

    // Cookie was set by the backend redirect — just refresh the user context
    (async () => {
      try {
        await refresh();
        navigate("/", { replace: true });
      } catch (e) {
        setError(formatApiError(e?.response?.data?.detail) || "Sign-in failed");
        setTimeout(() => navigate("/login", { replace: true }), 1800);
      }
    })();
  }, [refresh, navigate, searchParams]);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>
      <Header />
      <main className="flex-1 flex items-center justify-center">
        <div className="brutal-card p-8 bg-white text-center" data-testid="auth-callback-card">
          {error ? (
            <>
              <p className="font-heading text-xl font-bold text-brand-coral">{error}</p>
              <p className="text-sm mt-2 text-muted-foreground">Redirecting back to login…</p>
            </>
          ) : (
            <>
              <Loader2 className="w-10 h-10 animate-spin mx-auto" strokeWidth={3} />
              <p className="mt-4 font-heading text-lg font-bold">Signing you in…</p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
